import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ExternalLink, FileText, Calendar, Plus } from "lucide-react";
import { useContractProposals } from "@/lib/hooks/use-api";
import { format } from "date-fns";
import Link from "next/link";
import type { Client } from "@/lib/types/database";

interface ClientContractProposalsProps {
  client: Client;
  variant?: "default" | "compact";
  showTitle?: boolean;
}

export const ClientContractProposals: React.FC<ClientContractProposalsProps> = ({
  client,
  variant = "default",
  showTitle = true,
}) => {
  const { data: proposals, isLoading } = useContractProposals({
    contact_id: client.id,
    limit: 5,
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case "sent":
        return "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200";
      case "viewed":
        return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200";
      case "accepted":
        return "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200";
      case "rejected":
        return "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200";
      case "expired":
        return "bg-card-100 text-gray-800 dark:text-gray-200";
      default:
        return "bg-card-100 text-gray-800 dark:text-gray-200";
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), "MMM dd, yyyy");
    } catch {
      return "Invalid date";
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg">
            <FileText className="h-5 w-5" />
            Contract Proposals
          </CardTitle>
          <Button variant="outline" size="sm" asChild>
            <Link href={`/contracts?client=${client.id}`}>
              <Plus className="h-4 w-4 mr-1" />
              Generate Contract
            </Link>
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex justify-center py-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
          </div>
        ) : proposals && proposals.length > 0 ? (
          <div className="space-y-3">
            {proposals.map((proposal) => (
              <div key={proposal.id} className="border rounded-lg p-3 space-y-2">
                <div className="flex items-start justify-between">
                  <div className="space-y-1">
                    <h4 className="font-medium text-sm">{proposal.title}</h4>
                    <p className="text-xs text-muted-foreground">{proposal.description}</p>
                  </div>
                  <Badge className={`text-xs ${getStatusColor(proposal.status)}`}>
                    {proposal.status}
                  </Badge>
                </div>

                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    {formatDate(proposal.sent_at || proposal.created_at)}
                  </div>
                  {proposal.amount && (
                    <div>
                      ${parseFloat(proposal.amount).toFixed(2)} {proposal.currency}
                    </div>
                  )}
                </div>

                {proposal.file_url && (
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" asChild>
                      <a href={proposal.file_url} target="_blank" rel="noopener noreferrer">
                        <ExternalLink className="h-3 w-3 mr-1" />
                        View
                      </a>
                    </Button>
                  </div>
                )}
              </div>
            ))}

            {proposals.length >= 5 && (
              <div className="text-center pt-2">
                <Button variant="ghost" size="sm" asChild>
                  <Link href={`/contracts/manage?contact_id=${client.id}`}>View All Proposals</Link>
                </Button>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-6">
            <FileText className="mx-auto h-8 w-8 text-muted-foreground mb-2" />
            <p className="text-sm text-muted-foreground mb-3">
              No contract proposals found for this client
            </p>
            <Button variant="outline" size="sm" asChild>
              <Link href={`/contracts?client=${client.id}`}>
                <Plus className="h-4 w-4 mr-1" />
                Generate First Contract
              </Link>
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
